import { zodResolver } from '@hookform/resolvers/zod'
import {
  ApiError,
  type CreateProductAttributeDefinitionInput,
  createProductAttributeDefinitionSchema,
  type ProductAttributeDefinition,
  queryKeys,
} from '@mevaser/shared'
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  Input,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, SlidersHorizontal, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import type { z } from 'zod'
import { Page, PageHeader } from '../components/app-shell.js'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'

/**
 * Reusable suggestions for product attributes (t0919) — e.g. Color: Black,
 * Blue, Red. These only ever suggest a value on the product form: a product
 * can still save any value not listed here (Gold), and that never changes
 * this list. Renaming or deleting a definition never touches products that
 * already carry a value copied from it.
 */
export function AttributesPage() {
  const { can } = useAuth()
  const canWrite = can('catalog:write')
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<ProductAttributeDefinition | null>(null)
  const [deleting, setDeleting] = useState<ProductAttributeDefinition | null>(null)

  const { data: definitions, isLoading } = useQuery({
    queryKey: queryKeys.products.attributeDefinitions(),
    queryFn: () => api.get<ProductAttributeDefinition[]>('/product-attributes'),
  })

  return (
    <Page>
      <PageHeader
        title="Attributes"
        description="Reusable dropdown choices for product attributes, like Color: Black, Blue, Red. Products can still use a one-off value not on this list."
        actions={
          canWrite ? (
            <Button variant="primary" onClick={() => setCreating(true)}>
              <Plus /> New attribute
            </Button>
          ) : null
        }
      />

      <div className="overflow-hidden rounded-lg border border-border bg-surface">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-border bg-surface-sunken/60">
                {['Name', 'Choices', 'Order', ''].map((heading) => (
                  <th
                    key={heading}
                    className="px-3 py-2 text-start text-2xs font-medium uppercase tracking-wide text-foreground-muted"
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? null : definitions && definitions.length > 0 ? (
                definitions.map((definition) => (
                  <tr key={definition.id} className="border-b border-border last:border-0">
                    <td className="px-3 py-2 font-medium">{definition.name}</td>
                    <td className="px-3 py-2 text-foreground-muted">
                      {definition.choices.length > 0 ? definition.choices.join(', ') : '—'}
                    </td>
                    <td className="px-3 py-2 tabular-nums text-foreground-muted">
                      {definition.sortOrder}
                    </td>
                    <td className="px-3 py-2">
                      {canWrite ? (
                        <div className="flex gap-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setEditing(definition)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleting(definition)}
                          >
                            Delete
                          </Button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4}>
                    <EmptyState
                      icon={SlidersHorizontal}
                      title="No attributes yet"
                      description="Define reusable attributes — Color: Black, Blue, Red — to offer as suggestions on the product form."
                      action={
                        canWrite
                          ? { label: 'Add an attribute', onClick: () => setCreating(true) }
                          : undefined
                      }
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <CreateAttributeDialog open={creating} onOpenChange={setCreating} />
      <EditAttributeDialog definition={editing} onClose={() => setEditing(null)} />
      <DeleteAttributeDialog definition={deleting} onClose={() => setDeleting(null)} />
    </Page>
  )
}

type ChoiceRow = { id: number; value: string }

function choiceRowsFromList(choices: string[]): ChoiceRow[] {
  return choices.map((value, i) => ({ id: i, value }))
}

function choiceRowsToList(rows: ChoiceRow[]): string[] {
  return rows.map((r) => r.value.trim()).filter((v) => v !== '')
}

/**
 * The reusable choices for one attribute, edited as rows for the same reason
 * as AttributesEditor in products.tsx: a plain array can't hold an empty or
 * momentarily-duplicate entry while it's mid-typed.
 */
function ChoicesEditor({
  value,
  onChange,
}: {
  value: string[]
  onChange: (next: string[]) => void
}) {
  const nextId = useRef(0)
  const [rows, setRows] = useState<ChoiceRow[]>(() => {
    const initial = choiceRowsFromList(value)
    nextId.current = initial.length
    return initial
  })

  function commit(next: ChoiceRow[]) {
    setRows(next)
    onChange(choiceRowsToList(next))
  }

  return (
    <div className="space-y-2">
      {rows.map((row) => (
        <div key={row.id} className="flex items-center gap-2">
          <Input
            placeholder="Choice"
            value={row.value}
            onChange={(e) =>
              commit(rows.map((r) => (r.id === row.id ? { ...r, value: e.target.value } : r)))
            }
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Remove choice"
            onClick={() => commit(rows.filter((r) => r.id !== row.id))}
          >
            <X />
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={() => commit([...rows, { id: nextId.current++, value: '' }])}
      >
        <Plus /> Add choice
      </Button>
    </div>
  )
}

type FormValues = z.input<typeof createProductAttributeDefinitionSchema>

function CreateAttributeDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues, unknown, CreateProductAttributeDefinitionInput>({
    resolver: zodResolver(createProductAttributeDefinitionSchema),
    defaultValues: { choices: [], sortOrder: 100 },
  })

  useEffect(() => {
    if (!open) reset({ choices: [], sortOrder: 100 })
  }, [open, reset])

  const onSubmit = handleSubmit(async (values) => {
    try {
      await api.post('/product-attributes', values)
      await queryClient.invalidateQueries({ queryKey: queryKeys.products.all() })
      toast.success('Attribute created')
      onOpenChange(false)
    } catch (error) {
      toast.error(
        'Could not create the attribute',
        error instanceof ApiError ? error.message : undefined,
      )
    }
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New attribute</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit}>
          <DialogBody className="space-y-4">
            <Field label="Name" error={errors.name?.message} required>
              {(props) => <Input {...props} {...register('name')} invalid={!!errors.name} />}
            </Field>

            <Field
              label="Choices"
              error={errors.choices?.message}
              hint="Offered as a dropdown on the product form. Admins can still type a value not listed here."
            >
              {() => (
                <ChoicesEditor
                  value={watch('choices') ?? []}
                  onChange={(next) =>
                    setValue('choices', next, { shouldValidate: true, shouldDirty: true })
                  }
                />
              )}
            </Field>

            <Field
              label="Order"
              error={errors.sortOrder?.message}
              hint="Low numbers come first on the Attributes list."
            >
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  {...register('sortOrder', { valueAsNumber: true })}
                  invalid={!!errors.sortOrder}
                />
              )}
            </Field>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={isSubmitting}>
              Create attribute
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function EditAttributeDialog({
  definition,
  onClose,
}: {
  definition: ProductAttributeDefinition | null
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [choices, setChoices] = useState<string[]>([])
  const [sortOrder, setSortOrder] = useState('100')

  useEffect(() => {
    if (!definition) return
    setName(definition.name)
    setChoices(definition.choices)
    setSortOrder(String(definition.sortOrder))
  }, [definition])

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/product-attributes/${definition?.id}`, {
        name: name.trim(),
        choices,
        sortOrder: Number(sortOrder),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.products.all() })
      toast.success('Saved')
      onClose()
    },
    onError: (error: Error) =>
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined),
  })

  return (
    <Dialog open={definition !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit {definition?.name}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Name" required>
            {(props) => <Input {...props} value={name} onChange={(e) => setName(e.target.value)} />}
          </Field>

          <Field
            label="Choices"
            hint="Changing or removing a choice doesn't touch products that already use it."
          >
            {() => <ChoicesEditor key={definition?.id} value={choices} onChange={setChoices} />}
          </Field>

          <Field label="Order" hint="Low numbers come first on the Attributes list.">
            {(props) => (
              <Input
                {...props}
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function DeleteAttributeDialog({
  definition,
  onClose,
}: {
  definition: ProductAttributeDefinition | null
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()

  const remove = useMutation({
    mutationFn: () => api.delete(`/product-attributes/${definition?.id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.products.all() })
      toast.success('Attribute deleted')
      onClose()
    },
    onError: (error: Error) =>
      toast.error('Could not delete', error instanceof ApiError ? error.message : undefined),
  })

  return (
    <Dialog open={definition !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete {definition?.name}?</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <p className="text-sm">
            Products that already have this attribute keep their values. This only removes it as a
            suggestion going forward.
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" onClick={() => remove.mutate()} disabled={remove.isPending}>
            {remove.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
